import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? 'https://localhost:8000/api'
    : 'http://localhost:8000/api');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

export const storageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authTokenCreatedAt');
      localStorage.removeItem('authUser');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session_expired=1';
      }
    }
    return Promise.reject(error);
  },
);

export const productService = {
  getProducts: (params) => apiClient.get('/products-services', { params }),
  getProductDetail: (id) => apiClient.get(`/products-services/${id}`),
  getCategories: () => apiClient.get('/categories'),
  searchProducts: (query) => apiClient.get('/products-services/search', { params: { q: query } }),
  getBanners: () => apiClient.get('/banners'),
  getCoupons: () => apiClient.get('/coupons'),
  getServices: (params) =>
    apiClient.get('/products-services', { params: { ...params, type: 'service' } }),
  getServiceQuestions: (serviceId) => apiClient.get(`/products-services/${serviceId}/questions`),
  getNearbyProviders: (serviceId, params) =>
    apiClient.get(`/services/${serviceId}/nearby-providers`, { params }),
  askServiceQuestion: (serviceId, question) =>
    apiClient.post(`/products-services/${serviceId}/questions`, { question }),
  getServiceComments: (serviceId) => apiClient.get(`/products-services/${serviceId}/comments`),
  addServiceComment: (serviceId, comment, rating) =>
    apiClient.post(`/products-services/${serviceId}/comments`, { comment, rating }),
};

export const authService = {
  login: (email, password) => apiClient.post('/login', { login: email, password }),
  register: (data) => apiClient.post('/register', data),
  logout: async () => {
    try {
      await apiClient.post('/logout');
    } catch (e) {
      // ignore logout errors
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    window.location.href = '/';
  },
  refreshToken: () => apiClient.post('/refresh'),
  getUser: () => apiClient.get('/user'),
  extractToken: (response) => {
    return response.data?.token || response.data?.access_token;
  },
  extractUser: (response) => {
    return response.data?.user;
  },
  verifyOtp: (email, otp) => apiClient.post('/verify-otp', { email, otp }),
  resendOtp: (email) => apiClient.post('/resend-otp', { email }),
};

export const userService = {
  getProfile: () => apiClient.get('/user/profile'),
  updateProfile: (data) => apiClient.put('/user/profile', data),
  getDashboardStats: () => apiClient.get('/customer/dashboard-stats'),
  changePassword: (currentPassword, newPassword, newPasswordConfirmation) =>
    apiClient.post('/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: newPasswordConfirmation,
    }),
  getNotifications: () => apiClient.get('/user/notifications'),
  markNotificationAsRead: (id) => apiClient.post(`/notifications/${id}/mark-read`),
  markAllNotificationsAsRead: () => apiClient.post('/notifications/mark-all-read'),
  getNotificationSettings: () => apiClient.get('/user/notifications'),
  updateNotificationSettings: (data) => apiClient.put('/user/notifications', data),
  getPrivacySettings: () => apiClient.get('/user/privacy'),
  updatePrivacySettings: (data) => apiClient.put('/user/privacy', data),
  uploadAvatar: (formData) =>
    apiClient.post('/user/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

export const orderService = {
  getOrders: (params) => apiClient.get('/orders', { params }),
  getOrderDetail: (id) => apiClient.get(`/orders/${id}`),
  createOrder: (data) => apiClient.post('/orders', data),
  cancelOrder: (id) => apiClient.post(`/orders/${id}/cancel`),
  trackOrder: (id) => apiClient.get(`/orders/${id}/tracking`),
  getOrderHistory: () => apiClient.get('/customer/order-history'),
  getSpendingAnalytics: () => apiClient.get('/customer/spending-analytics'),
  getDeliveryLocation: (orderId) => apiClient.get(`/delivery/location/by-order/${orderId}`),
};

export const cartService = {
  getCart: () => apiClient.get('/cart'),
  addToCart: (productId, quantity = 1, bookingData = {}) =>
    apiClient.post('/cart/items', { product_service_id: productId, quantity, ...bookingData }),
  updateCartItem: (id, quantity) => apiClient.put(`/cart/items/${id}`, { quantity }),
  removeFromCart: (id) => apiClient.delete(`/cart/items/${id}`),
  clearCart: () => apiClient.delete('/cart'),
  applyCoupon: (code) => apiClient.post('/cart/apply-coupon', { coupon_code: code }),
};

export const wishlistService = {
  getWishlist: () => apiClient.get('/wishlists'),
  addToWishlist: (productId) => apiClient.post('/wishlists', { product_service_id: productId }),
  removeFromWishlist: (id) => apiClient.delete(`/wishlists/${id}`),
  clearWishlist: () => apiClient.delete('/wishlists'),
};

export const walletService = {
  getWallet: () => apiClient.get('/wallet'),
  addBalance: (amount) => apiClient.post('/wallet/add-balance', { amount }),
  getTransactions: () => apiClient.get('/wallet/transactions'),
  verifyKhalti: (data) => apiClient.post('/wallet/verify-khalti', data),
};

export const addressService = {
  getAddresses: () => apiClient.get('/addresses'),
  addAddress: (data) => apiClient.post('/addresses', data),
  updateAddress: (id, data) => apiClient.put(`/addresses/${id}`, data),
  deleteAddress: (id) => apiClient.delete(`/addresses/${id}`),
  setDefaultAddress: (id) => apiClient.put(`/addresses/${id}/set-default`),
};

export const reviewService = {
  getReviews: (params) => apiClient.get('/reviews', { params }),
  createReview: (data) => apiClient.post('/reviews', data),
  updateReview: (id, data) => apiClient.put(`/reviews/${id}`, data),
  deleteReview: (id) => apiClient.delete(`/reviews/${id}`),
  getReviewableItems: () => apiClient.get('/reviewable-items'),
  getProductQuestions: (productId) => apiClient.get(`/products-services/${productId}/questions`),
  askProductQuestion: (productId, question) =>
    apiClient.post(`/products-services/${productId}/questions`, { question }),
  getProductComments: (productId) => apiClient.get(`/products-services/${productId}/comments`),
  addProductComment: (productId, comment, rating) =>
    apiClient.post(`/products-services/${productId}/comments`, { comment, rating }),
};

export const notificationService = {
  getNotifications: () => apiClient.get('/notifications'),
  markAsRead: (id) => apiClient.post(`/notifications/${id}/mark-read`),
  markAllAsRead: () => apiClient.post('/notifications/mark-all-read'),
  getUnreadCount: () => apiClient.get('/notifications/unread-count'),
  updateDeviceToken: (token) =>
    apiClient.post('/notifications/update-device-token', { device_token: token }),
};

export const couponService = {
  getCoupons: () => apiClient.get('/coupons'),
  validateCoupon: (code, cartTotal) =>
    apiClient.post('/coupons/validate', { code, cart_total: cartTotal }),
  getUserCoupons: () => apiClient.get('/user-coupons'),
};

export const loyaltyService = {
  getLoyaltyPoints: () => apiClient.get('/loyalty-points'),
  redeemPoints: (points, redemptionOption) =>
    apiClient.post('/loyalty-points/redeem', { points, redemption_option: redemptionOption }),
  getLoyaltyHistory: () => apiClient.get('/loyalty-points/history'),
  getRedemptionOptions: () => apiClient.get('/loyalty-points/redemption-options'),
};

export const referralService = {
  getReferralInfo: () => apiClient.get('/referral/info'),
  validateReferral: (code) => apiClient.post('/referral/validate', { referral_code: code }),
  applyReferral: (code) => apiClient.post('/referral/apply', { referral_code: code }),
  getReferralStats: () => apiClient.get('/customer/referral-stats'),
  shareReferral: () => apiClient.post('/referral/share'),
};

export const membershipService = {
  getMembershipBenefits: () => apiClient.get('/membership/benefits'),
  getMembershipProgress: () => apiClient.get('/membership/progress'),
  upgradeMembershipTier: (data) => apiClient.post('/membership/upgrade-tier', data),
  applyMembershipDiscount: () => apiClient.post('/membership/apply-discount'),
  getSubscriptions: () => apiClient.get('/subscriptions'),
  getUserSubscriptions: () => apiClient.get('/user-subscriptions'),
  createMembershipRequest: (data) => apiClient.post('/membership-requests', data),
  getUserMembershipRequests: () => apiClient.get('/membership-requests/user'),
  confirmMembershipPayment: (id, data) =>
    apiClient.post(`/membership-requests/${id}/payment`, data),
};

export const chatService = {
  getConversations: () => apiClient.get('/conversations'),
  getChatMessages: (userId) =>
    apiClient.get('/chat-messages', { params: { conversation_with: userId } }),
  sendMessage: (data) => apiClient.post('/chat-messages', data),
  markMessagesAsRead: (data) => apiClient.post('/messages/mark-read', data),
  shareLiveLocation: (location) => apiClient.post('/chat/share-live-location', location),
  stopLiveLocation: (id) => apiClient.post(`/live-location/${id}/stop`),
};

export const paymentService = {
  getPaymentSettings: () => apiClient.get('/payment/settings'),
  generateQrCode: (amount) => apiClient.post('/qr-code/generate', { amount }),
  getOrderQrCode: (orderId) => apiClient.get(`/orders/${orderId}/qr-code`),
  submitPaymentProof: (orderId, data) => apiClient.post(`/orders/${orderId}/payment-proof`, data),
  getPaymentDetails: (orderId) => apiClient.get(`/orders/${orderId}/payment-details`),

  processEsewaPayment: (data) => apiClient.post('/payment/esewa/process', data),
  processKhaltiPayment: (data) => apiClient.post('/payment/khalti/process', data),
  getInvoice: (orderId) => apiClient.get(`/billing/customer-invoice/${orderId}`),
  downloadInvoice: (orderId) =>
    apiClient.get(`/billing/customer-invoice/${orderId}/download`, { responseType: 'blob' }),
};

export const newsletterService = {
  subscribe: (email, source) => apiClient.post('/newsletter/subscribe', { email, source }),
  unsubscribe: (email) => apiClient.get(`/newsletter/unsubscribe/${email}`),
};

export const currencyService = {
  getCurrencies: () => apiClient.get('/currencies'),
};

export const offerService = {
  getOffers: () => apiClient.get('/offers'),
};

export const giftCertificateService = {
  getCertificates: () => apiClient.get('/gift-certificates'),
  purchase: (data) => apiClient.post('/gift-certificates', data),
  redeem: (code) => apiClient.post('/gift-certificates/redeem', { code }),
  validateCode: (code) => apiClient.post('/gift-certificates/validate', { code }),
  delete: (id) => apiClient.delete(`/gift-certificates/${id}`),
};

export const affiliateService = {
  getDashboard: () => apiClient.get('/affiliate/dashboard'),
  getLinks: () => apiClient.get('/affiliate/links'),
  createLink: (data) => apiClient.post('/affiliate/links', data),
  updateLink: (id, data) => apiClient.put(`/affiliate/links/${id}`, data),
  toggleLink: (id) => apiClient.patch(`/affiliate/links/${id}/toggle`),
  deleteLink: (id) => apiClient.delete(`/affiliate/links/${id}`),
};

export const twoFactorService = {
  generateSecret: () => apiClient.get('/generate-2fa-secret'),
  setup: (code, password) => apiClient.post('/setup-2fa', { code, password }),
  toggle: (enable) => apiClient.post('/toggle-2fa', { enable }),
  disable: (password) => apiClient.post('/disable-2fa', { password }),
  verifyLogin: (userId, code) => apiClient.post('/verify-2fa-login', { user_id: userId, code }),
};

export const bookingService = {
  getUpcoming: (params) => apiClient.get('/bookings/upcoming', { params }),
  getPast: (params) => apiClient.get('/bookings/past', { params }),
  getStatistics: () => apiClient.get('/bookings/statistics'),
  getAvailableSlots: (params) => apiClient.get('/bookings/available-slots', { params }),
  getBookings: (params) => apiClient.get('/bookings', { params }),
  createBooking: (data) => apiClient.post('/bookings', data),
  cancelBooking: (id) => apiClient.delete(`/bookings/${id}`),
  confirmBooking: (id) => apiClient.post(`/bookings/${id}/confirm`),
  getBookingDetails: (id) => apiClient.get(`/bookings/${id}`),
};

export const deliveryLocationService = {
  getDeliveryLocationByOrder: (orderId) => apiClient.get(`/delivery/location/by-order/${orderId}`),
  getCurrentLocation: (deliveryManId) =>
    apiClient.get(`/delivery/location/current/${deliveryManId}`),
};

export const contactService = {
  submit: (data) => apiClient.post('/contact', data),
};

export default apiClient;
